<template>
<div
  class='activity_activity-overlay'
  :class='main_class'
  role='dialog'
  aria-modal='true'
  :aria-label='activity.headline || activity.subheadline'
>
<div
  id='activity-overlay-content'
  class='activity_activity-overlay_inner'
>
  <div
    class='activity_activity-overlay_modal-restricter'
    tabindex='0'
    @focus='setFocus'
  />

  <button
    v-if='show_close_button'
    ref='close-button'
    title='Close'
    aria-label='Close'
    class='activity_activity-overlay_close'
    @click.prevent='closeOverlay'
  >
    <CloseIcon />
  </button>

  <!-- Self Assessment V2 -->
  <template v-if='activity.type === "activity_v2_self_assessments"'>
    <SelfAssessmentActivity
      :api_client='api_client'
      :activity='activity'
      :slide_result='slide_result'
      :toggle='toggleActivityOverlay'
      :updateResult='updateResult'
    />
  </template>

  <!-- Quiz V1 -->
  <template v-if='activity.type === "activity_v1_quizzes" || activity.type === "activity_v1_self_assessments"'>
    <QuizActivity
      :slide_result='slide_result'
      :updateResult='updateResult'
      :finish='skipActivity'
      :quiz='activity'
    />
  </template>

  <!-- PairedComparison V1 -->
  <template v-if='activity.type === "activity_v1_paired_comparisons"'>
    <PairedComparisonActivity
      :activity='activity'
      :finish='skipActivity'
      :slide_result='slide_result'
      :toggle='toggleActivityOverlay'
      :updateResult='updateResult'
    />
  </template>

  <!-- Article -->
  <template v-if='activity.type === "activity_v1_articles"'>
    <ArticleActivity
      :activity='activity'
      :toggle='toggleActivityOverlay'
      :goToNextSlide='goToNextSlide'
    />
  </template>

  <!-- Polls -->
  <template v-if='activity.type === "activity_v1_polls"'>
    <PollActivity
      :activity='activity'
      :slide_result='slide_result'
      :toggle="toggleActivityOverlay"
      :updateResult='updateResult'
      :toggleSignInOverlay='toggleSignInOverlay'
      :goToNextSlide='goToNextSlide'
      :is_authenticated='is_authenticated'
      :updateCurrentActivity='updateCurrentActivity'
    />
  </template>

  <!-- Journal -->
  <template v-if='activity.type === "activity_v1_journals"'>
    <JournalActivity
      :slide_result='slide_result'
      :activity='activity'
      :api_client='api_client'
      :toggle='toggleActivityOverlay'
      :updateResult='updateResult'
      :goToNextSlide='goToNextSlide'
    />
  </template>

  <!-- Discussion -->
  <template v-if='activity.type === "activity_v1_discussions"'>
    <DiscussionActivity
      :discussion_topic_id='activity.topic_id'
      :api_client='api_client'
      :label='activity.label'
      :updateResult='updateResult'
    >
      <a
        href="#"
        class='slide-discussion_keep button-one-branded'
        @click.prevent='skipActivity'
      >
        {{ $t('keep_learning') }}
      </a>
    </DiscussionActivity>
  </template>

  <!-- Assessments aka Knowledge Check -->
  <template v-if='activity.type === "activity_v1_assessments"'>
    <AssessmentActivity
      :api_client='api_client'
      :activity='activity'
      :slide='current_slide'
      :slide_result='slide_result'
      :assessment_results_opened='assessment_results_opened'
      :updateResult='updateResult'
      :updateCurrentActivity='updateCurrentActivity'
      :toggle='toggleActivityOverlay'
    />
  </template>

  <!-- Intention setter -->
  <template v-if='activity.type === "activity_v1_intention_setters"'>
    <IntentionActivity
      :slide='current_slide'
      :slide_result="slide_result"
      :activity='activity'
      :toggle='toggleActivityOverlay'
      :updateResult='updateResult'
      :is_authenticated='is_authenticated'
      :api_client='api_client'
    >
      <a
        href="#"
        class='action-one-branded'
        @click.prevent='skipActivity'
      >
        {{ $t('close') }}
      </a>
    </IntentionActivity>
  </template>

  <div
    class='activity_activity-overlay_modal-restricter'
    tabindex='0'
    @focus='setFocus'
  />
</div>
</div>
</template>

<script>
import CloseIcon from '../icons/close.svg'

import ArticleActivity from './activities/v1/Article/article-v1-overlay'
import AssessmentActivity from './activities/v1/Assessment/assessment-v1-overlay'
import SelfAssessmentActivity from './activities/v2/SelfAssessment/SelfAssessmentV2'
import QuizActivity from './activities/v1/Quiz/quiz-v1'
import PairedComparisonActivity from './activities/v1/PairedComparison/paired-comparison-v1'
import PollActivity from './activities/v1/Poll/poll-v1-overlay'
import DiscussionActivity from './activities/v1/Discussion/DiscussionV1'
import IntentionActivity  from './activities/v1/IntentionSetter/IntentionSetterV1'
import JournalActivity    from './activities/v1/Journal/journal-v1-overlay'

export default {
  props: [
    'toggleActivityOverlay',
    'toggleSignInOverlay',
    'api_client',
    'activity',
    'assessment_results_opened',
    'current_slide',
    'slide_result',
    'updateResult',
    'updateCurrentActivity',
    'goToNextSlide',
    'is_authenticated'
  ],

  components: {
    ArticleActivity,
    AssessmentActivity,
    SelfAssessmentActivity,
    QuizActivity,
    DiscussionActivity,
    PairedComparisonActivity,
    PollActivity,
    IntentionActivity,
    JournalActivity,

    CloseIcon
  },

  data() {
    return {
      shift_is_pressed: false,
      keyUpCallback: (e) => this.detectKeyup(e),
      keyDownCallback: (e) => this.detectKeydown(e),
      lastFocusedSelector: null
    }
  },

  mounted() {
    // Store classes of the element that initiated the overlay
    const whatClasses = $('html').attr('data-whatclasses')
    let lastFocusedClasses = []
    if (whatClasses) lastFocusedClasses = whatClasses.split(',')
    if (lastFocusedClasses.length) {
      this.lastFocusedSelector = '.' + lastFocusedClasses.join('.')
    }

    this.setFocus()

    document.body.addEventListener('keyup', this.keyUpCallback)
    document.body.addEventListener('keydown', this.keyDownCallback)
  },

  beforeUnmount() {
    document.body.removeEventListener('keyup', this.keyUpCallback)
    document.body.removeEventListener('keydown', this.keyDownCallback)
  },

  computed: {
    show_close_button() {
      switch (this.activity.type) {
        case 'activity_v1_intention_setters':
        case 'activity_v2_self_assessments':
          return false
        default:
          return true
      }
    },

    main_class() {
      const classes = []

      if (this.activity.type === 'activity_v1_articles') {
        classes.push('activity_activity-overlay--article')
      }
      return classes
    }
  },

  methods: {
    closeOverlay() {
      if (this.lastFocusedSelector) {
        $(this.lastFocusedSelector).focus()
      }
      this.toggleActivityOverlay()
    },

    skipActivity() {
      if (typeof this.goToNextSlide !== 'undefined') this.goToNextSlide()
      this.toggleActivityOverlay()
    },

    setFocus() {
      setTimeout(() => {
        // double check to avoid console errors
        this.$refs['close-button'] && this.$refs['close-button'].focus()
      }, 100)
    },

    detectKeyup(e) {
      if ((e.key !== undefined && e.key === 'Escape') ||
        (e.keyCode !== undefined && e.keyCode === 27)) {
        this.toggleActivityOverlay()
      }

      if (e.key === 'Shift') {
        this.shift_is_pressed = false
      }
    },

    detectKeydown(e) {
      if (e.key === 'Shift') {
        this.shift_is_pressed = true
      }
    }

  }
}
</script>

<style lang='sass' scoped>
@import './activity-overlay'
</style>
